import { ParamMap } from '@angular/router';
import * as dayjs from 'dayjs';
import { EsgTypes, IncomeTypes, InstrumentCategories, InstrumentTargets, InstrumentTypes, LineChartCoordinates, ModelStrategyInstrumentOut } from 'src/app/shared/charts/charts';
import { hooverTypes } from 'src/app/shared/charts/custom-hover-tooltip';
import { ExpectedOwnedStrategy } from 'src/app/shared/owned-strategy-card/strategy';
import { ConsultantModalInput } from '../modal/modal';
import { SafeUrl } from '@angular/platform-browser';

export type UserTypes = 'client';

export interface ApiExpectedStrategy {
  name: string,
  currencyCode: string,
  instruments: Record<string, number>,
  investmentFrequency: 'ONE_TIME' | 'CONTINUOUS',
  initialDeposit: number,
  regularDeposit: number,
  weeksBetweenInvestments: number, // 1 | 4 | 12 weeks
  totalYearsToInvest: number,
  rebalance: boolean,
  frameworkContractId: number,
  newClientDipSelected?: boolean,
  efektika?: boolean,
}

export interface ApiUpdateStrategy {
  instruments: Record<string, number>,
  rebalance: boolean,
  newPortfolioName: string,
  initialDeposit: number,
  regularDeposit: number,
  weeksBetweenInvestments: InvestmentFrequency,
  totalYearsToInvest: number,
}

export interface ApiEfektikaUpdateStrategy {
  rebalance: boolean,
  newEfektikaName: string,
}

export interface InvestmentListItem {
  id: number,
  name: string,
  type: 'portfolio' | 'strategy',
  dip: boolean,
}

export interface InvestmentEdit {
  name: string,
  initialDeposit: number,
  regularDeposit: number,
  currencyCode: string,
  weeksBetweenInvestments: number,
  investmentFrequency: 'ONE_TIME' | 'CONTINUOUS',
  totalYearsToInvest: number,
}

export interface ApiSummarySum {
  sumOfDeposits: number,
  sumOfWithdrawals: number,
  actualYield: number,
  nextRebalancingDate: string,
  portfolios: ExpectedOwnedStrategy[],
  totalBalance: number,
}

export enum InvestmentFrequency {
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY'
}

export const investmentFrequencyMap = new Map<InvestmentFrequency, number>([
    [InvestmentFrequency.WEEKLY, 1],
    [InvestmentFrequency.MONTHLY, 4],
    [InvestmentFrequency.QUARTERLY, 12]
  ]);

export enum ModelStrategyTypes {
  THEMATIC_STRATEGY = 'THEMATIC_STRATEGY',
  MODEL_PORTFOLIO = 'MODEL_PORTFOLIO',
  OKS = 'OKS',
}

export interface ApiModelStrategy {
  modelStrategyId: number,
  name: string,
  description: string,
  imageUrl: string,
  iconUrl: string,
  translate?: true // FE flag only
  uiOrder?: number | null,
  type: ModelStrategyTypes,
  efektika?: boolean,
}

export interface ModelStrategyOrPortfolioInstrumentOut extends ApiModelStrategy {
  instruments: ModelStrategyInstrumentOut[];
}

export interface ApiNotificationsCount {
  totalSum: number,
  messageSum: number,
  noticeSum: number,
}

export enum ManagementTypesEnum {
  PORTFOLIO_MANAGEMENT = 'PORTFOLIO_MANAGEMENT',
  EXECUTION_ONLY = 'EXECUTION_ONLY',
}

export enum FrameworkContractType {
  STANDARD = 'STANDARD',
  DIP = 'DIP'
}

export interface ApiClientAccountOut {
  clientAccountId: number,
  titleBefore: string | null,
  firstName: string,
  lastName: string,
  titleAfter: string | null,
  birthDate: string,
  personalIdentificationNumber: string,
  citizenShipCode: string,
  phoneNumber?: string,
  maxRiskScore: number,
  email?: string,
  addresses: ApiAddressOut[],
  currencyCode: string,
  canReadEnglish: boolean,
  createdDate: string, //ex. '2023-05-25'
  managementType: keyof typeof ManagementTypesEnum;
  hasOpenOrder: boolean,
  bcasClientId: string,
  validContracts: FrameworkContractOut[],
  investmentQuestionnaire: InvestmentQuestionnaire,
  disclaimerShownAt: string | null,
  opfTransferAllowedAt?: string,
  opfTransferAt?: string,
}

export interface UnsignedAddition {
  frameworkContractAdditionId: number,
  type: 'MN_415'
}

export interface FrameworkContractOut {
  frameworkContractId: number,
  frameworkContractNumber: string,
  dip: boolean,
  inLiquidation: boolean,
  unsignedAdditions: UnsignedAddition[],
  invSignDate: string, // ex. '2023-05-25'
  opfFeeImplementation: boolean,
}

interface InvestmentQuestionnaire {
  date: string
  validityDate: string
  cooldownDate: string
  expired: boolean
  updatable: boolean
  instrumentCategories: InstrumentCategories[],
}

export interface ApiAddressOut {
  type: 'DOMICILE' | 'POSTAL' | 'ORIGIN',
  streetName: string,
  descriptionNo: string,
  orientationNo: string | null,
  city: string,
  zip: string,
  countryCode: string,
}

export interface ApiAvailableCash {
  instrumentId: number,
  isin: string,
  currencyCode: string,
  cash: boolean,
}

export interface StrategyInitData {
  params: ParamMap,
  availableInstruments: InstrumentDetailFullOut[],
  availableCash: ApiAvailableCash[],
  limits: StrategyLimitOut,
}

export interface StrategyLimitOut {
  minCashThreshold: number,
  minInstrumentThreshold: number,
  minCashRebalancedMonthlyThreshold: number,
}

export interface DetailInitData {
  params: ParamMap,
  queryParams: ParamMap,
  availableInvestments: ExpectedOwnedStrategy[],
  drafts: ExpectedOwnedStrategy[],
}

export interface WorkplaceDetailInitData {
  params: ParamMap,
  queryParams: ParamMap,
  availableInvestments: ExpectedOwnedStrategy[],
}

export interface ApiPagedTransaction {
  content: ApiTransaction[],
  totalCount: number,
}

export interface ApiTransaction {
    transactionId: string,
    portfolio: {
      portfolioId: number,
      name: string,
      dip?: boolean,
      accessible: boolean
    },
    instrument: {
      instrumentId: number,
      isin: string,
      currencyCode: string,
      cash: boolean,
    }
    direction: ApiTransactionDirections,
    type: ApiTransactionTypes | null,
    quantity: number,
    transactionTimestamp: string,
    description: string,
    amount: number,
    uiType?: ApiUiTransactionTypes,
    vat: number
}

export enum ApiTransactionDirections {
  DEBIT = 'DEBIT',
  CREDIT = 'CREDIT'
}

export enum ApiUiTransactionTypes {
  DEPOSIT = 'DEPOSIT',
  WITHDRAWAL = 'WITHDRAWAL',
  BUY = 'BUY',
  SELL = 'SELL',
  FEE = 'FEE',
  DIVIDEND = 'DIVIDEND',
  OPF_MANAGEMENT_FEE_REFUND = 'OPF_MANAGEMENT_FEE_REFUND',
  ROUNDING_RETURN = 'ROUNDING_RETURN',
  FX_TRANSACTION = 'FX_TRANSACTION'
}

export enum ApiTransactionTypes {
  ORDER = 'ORDER',
  MONEY = 'MONEY',
  FEE = 'FEE',
  OPF_MANAGEMENT_FEE_REFUND = 'OPF_MANAGEMENT_FEE_REFUND',
  ROUNDING_RETURN = 'ROUNDING_RETURN',
}

export interface PaginatedTransactionInput {
  [index: string]: unknown,
  portfolioId?: number,
  transactionTimestampFrom?: string, //ex. 2023-04-02T20:00:07.433Z
  transactionTimestampTo?: string, //ex. 2023-04-02T20:00:07.433Z
  type?: Record<number, keyof typeof TransactionTypesEnum | keyof typeof ApiTransactionTypes>,
  page?: number, // from 0
  size?: number, // default 10
  clientId?: number,
}

export enum TransactionTypesEnum {
  DEPOSIT = 'DEPOSIT',
  WITHDRAWAL = 'WITHDRAWAL',
  BUY = 'BUY',
  SELL = 'SELL',
  FEE = 'FEE',
  DIVIDEND = 'DIVIDEND',
  OPF_MANAGEMENT_FEE_REFUND = 'OPF_MANAGEMENT_FEE_REFUND',
  RETURN = 'RETURN',
  FX_TRANSACTION = 'FX_TRANSACTION',
}

export enum AvailablePeriodsEnum {
  ALL = 'ALL',
  YTD = 'YTD',
  Y5 = '5y',
  Y1 = '1y',
  M6 = '6m',
  M3 = '3m',
  D30 = '30d',
}

export enum AvailableChartCurrenciesEnum {
  CZK = 'CZK',
  PERCENT = '%',
}

export enum AvailableChartPeriodsEnum {
  ALL = 'ALL',
  YTD = 'YTD',
  Y5 = 5,
  Y3 = 3,
  Y1 = 1,
}


export interface ApiDocument {
  type: 'confirmation' | 'listing' | 'other',
  date: string,
  name: string,
  fileUrl?: string,
  documentId: number,
  order?: number | null,
}


export interface NewPortfolioInput {
  name: string,
  initialDeposit: number,
  regularDeposit: number,
  totalYearsToInvest: number,
  riskScore: number,
  weeksBetweenInvestments: number,
  frameworkContractId: number,
  expectedValue?: LineChartCoordinates,
  newClientDipSelected?: boolean,
}

export interface NotificationApiResponse {
  totalCount: number,
  content: ApiNotification[],
}

export interface ApiNotification {
  notificationWebId: number,
  notificationTypeCode: 'ADDED_DEPOSIT',
  notificationWebStatus: keyof typeof NotificationsStatusEnum,
  createdDate: string, // ex. 2023-04-13T03:36:20.499Z
  notificationKind: 'MESSAGE' | 'NOTICE',
  htmlMessage: string,
  subject: string,
}

export enum NotificationsStatusEnum {
  NEW = 'NEW',
  PROCESSED = 'PROCESSED',
  RESOLVED = 'RESOLVED',
  ERROR = 'ERROR',
}

export interface ModelPortfolioInstruments {
    instrument: InstrumentDetailFullOut,
    weight: number,
  }

export interface PortfolioOut {
  portfolioId: number,
  name: string,
  portfolioType: 'PORTFOLIO' | 'STRATEGY',
  modelPortfolioInstruments: ModelPortfolioInstruments[],
  portfolioChangeAffectsFees: boolean,
  dip: boolean,
}


export interface ApiCalendar {
  rebalancingDates: RebalancingDateOut[],
  calendarEvents: CalendarEventOut[],
}

interface RebalancingDateOut {
  rebalancingDateId: number,
  runningDate: string,
  rebalancingType: 'QUARTERLY' | 'REGULARLY',
  rebalancedMonthly: boolean,
}

interface CalendarEventOut {
  calendarEventId: number,
  calendarEventType: keyof typeof CalendarEventTypesApi,
  eventDate: string,
}

export enum CalendarEventTypesApi {
  CZ_PUBLIC_HOLIDAY = 'CZ_PUBLIC_HOLIDAY',
  US_PUBLIC_HOLIDAY = 'US_PUBLIC_HOLIDAY',
  UK_PUBLIC_HOLIDAY = 'UK_PUBLIC_HOLIDAY',
}

export interface ApiModelPortfolioOut {
  riskScore: number,
  name: string,
  instruments: ModelStrategyInstrumentOut[],
  description?: string,
}

export interface ApiRegularWithdrawal {
  regularWithdrawalId: number,
  portfolioId: number,
  portfolioName: string,
  bankAccount: {
    bankAccountId: number,
    accountNumber: string,
    bankCode: string,
  },
  mode: RegularWithdrawalModes,
  value: number,
  frequency: RegularWithdrawalFrequencies,
  status?: RegularWithdrawalStatus,
  validFrom: string,
  validTo?: string,
  nextExecutionDate: string,
  sumOfWithdrawals: number,
}

export enum RegularWithdrawalModes {
  FIXED_AMOUNT = 'FIXED_AMOUNT',
}

export enum RegularWithdrawalDraftStatus {
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  PENDING_DIP_APPROVAL = 'PENDING_DIP_APPROVAL',
  CREATED = 'CREATED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  CANCELLED = 'CANCELLED',
  EXPIRED = 'EXPIRED',
}

export enum RegularWithdrawalFrequencies {
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
}

export enum RegularWithdrawalStatus {
  PENDING_CONFIRMATION,
  ACTIVE,
  CANCELLED,
  EXPIRED,
  ERROR,
  INVALID_PORTFOLIO_STATE,
}

export interface ApiWithdrawalPortfolio {
  portfolioId: number,
  name: string,
  portfolioType: string,
  dip: boolean,
  balance: number,
}

export interface ApiRegularWithdrawalLimit {
  maxAllowedWithdrawal: number,
  minAllowedWithdrawal: number,
  currency: string,
}

export interface NewRegularWithdrawalInput {
  portfolioId: number,
  bankAccountId: number,
  mode: RegularWithdrawalModes,
  value: number,
  frequency: RegularWithdrawalFrequencies,
  validTo?: string // eg. 2025-12-03
}

export interface DepositRegularity {
  key: string,
  value: string,
};

export interface ModelPortfolioChartInput {
  depositRegularity: DepositRegularity,
  oneTimeDeposit: number,
  timeOfInvestment: number,
  regularDeposit: number,
  riskProfile: number,
  name?: string,
}

export interface ModelStrategyChartInput {
  weeksBetweenInvestments: number,
  yearsToInvest: number,
  initialDeposit: number,
  regularDeposit: number,
  isinToWeightMap: Record<string, number>,
}

export interface ClientPortfolioEntryFeeCalcInput {
  name: string,
  oneTimeDeposit: number,
  regularDeposit: number,
  timeOfInvestment: number,
  rebalancing: boolean,
  instrumentsWeights: Map<string, number>,
  depositRegularity: DepositRegularity,
  dip: FrameworkContractOut
  riskProfile: number,
}

export interface ApiModelPortfolioChartOut {
  [index: string]: ChartPoint[];
  optimistic: ChartPoint[],
  expected: ChartPoint[],
  pessimistic: ChartPoint[],
  pureInvestment: ChartPoint[],
}

export interface ChartPoint {
  x: number, // Timestamp as unix time (seconds since 1 January 1970 00:00:00 UTC).
  y: number, // Value/price.
  depositValue?: number,
}

export interface ChartPointTyped extends ChartPoint {
  type?: keyof typeof hooverTypes,
}

export interface PortfolioDepositOut {
  accountNumber: string,
  bankCode: string,
  iban: string,
  currency: 'CZK' | 'EUR',
  regularDeposit: number,
  variableSymbol: string,
  entryFeeInfo?: EntryFeeInfoOut,
  specificSymbols: SpecificSymbol[],
}

interface SpecificSymbol {
  type: SpecificSymbolTypes,
  symbol: string,
}

enum SpecificSymbolTypes {
  INITIAL,
  REGULAR,
  EMPLOYER_INITIAL,
  EMPLOYER_REGULAR,
  REFUND_ENTRY,
  REFUND_MANAGEMENT,
}

interface EntryFeeInfoOut {
  entryFeeAlreadyPaid: null | number,
  totalEntryFee: null | number,
  unpaidAmountWithVat: null | number,
  vatAlreadyPaid: null | number,
  entryFeeAlreadyPaidWithVat: null | number,
}

export interface ApiPortfolioSummaryLiteOut {
  portfolioId: number,
  name: string,
  portfolioType: 'PORTFOLIO' | 'STRATEGY',
  dip: boolean,
}

export interface ApiSummaryChartOut {
  priceInTime: ChartPoint[],
  depositsInTime: ChartPoint[],
  moneyTransactionsInTime: MoneyPoint[],
}

export interface MoneyPoint {
  x: number,
  sumOfWithdrawals: number,
  sumOfDeposits: number,
}

export interface ClientBankAccountOut {
  clientBankAccountId: number,
  accountNumber: string,
  bankCode: string,
  currencyCode: string,
  isPreferred: boolean,
}

export interface WithdrawInput {
  portfolioId: number,
  clientBankAccountId: number,
  amount: number,
}

export interface SummaryChartInput {
  currencyCode?: 'CZK' | 'EUR' | string,
  fromDate?: Date | dayjs.Dayjs,
  toDate?: Date | dayjs.Dayjs,
  size?: number,
  priceMeasure?: PriceMeasurements,
  clientId?: number,
}

export enum DocumentCategory {
  REPORT = 'REPORT',
  ACKNOWLEDGEMENT = 'ACKNOWLEDGEMENT',
  OTHER = 'OTHER',
}
export interface DocumentInput {
  [index: string]: unknown,
  issuedFrom?: string, // ex. '2000-10-31'
  issuedTo?: string, // ex. '2000-10-31'
  category?: DocumentCategory[],
  page?: number | string,
  size?: number | string,
}

export interface PagedResultDocumentOut {
  content: DocumentOut[],
  totalCount: number,
}

export interface DocumentOut {
  documentId: number,
  category: DocumentCategory,
  description: string,
  contentType: string,
  issuedAt: string,
}

export interface ClientCollaboratorOut {
  collaboratorId: number,
  email: string,
  name: ColName,
  phone: ColPhone,
  hasPhoto?: boolean,
}

interface ColName {
  firstName: string,
  lastName: string,
  titleBefore: string,
  titleAfter: string,
}

interface ColPhone {
  prefix: string,
  number: string,
}

export interface AllInvestmentsListItem {
  name: string,
  portfolioId: number,
  isLiquidated: boolean,
  depositExists: boolean,
}

export interface PageRequest {
  [index: string]: number,
  page: number; // from 0
  size: number;
}

export interface BlogArticleDetail {
  blogId: number;
  title: string;
  perex: string;
  mainPhoto?: BlogMainPhoto;
  publishOn: string;
  readingTime: number;
  freeContent?: string;
  premiumContent?: string;
}

export interface BlogArticlesPage {
  content: BlogArticleDetail[],
  totalCount: number,
}

export interface BlogMainPhoto {
  url?: string;
  alt?: string;
  copyright?: string;
}

export interface InstrumentPredictionOut {
  [index: string]: InstrumentPredictionIntervalOut;
  oneYear: InstrumentPredictionIntervalOut,
  threeYears: InstrumentPredictionIntervalOut,
  fiveYears: InstrumentPredictionIntervalOut,
}

export interface InstrumentPredictionIntervalOut {
  graph: GraphPoint[],
  performances: InstrumentPerformance[],
  cumulativePriceAction: number,
}

export interface InstrumentPerformance {
  instrumentId: number,
  performance: number,
}

export interface GraphPoint {
  x: number,
  y: number,
}

export interface NewClient {
  name: string,
  surname: string,
  phone?: string,
  email?: string,
}

export type InvestmentGoals = 'ANNUITY_IN_RETIREMENT' | 'FOR_KIDS' | 'FOR_HOUSING' | 'CREATION_OF_RESERVE' | 'OWN_TARGET' | 'WITHOUT_TARGET';
export enum DraftInitiator { COLLABORATOR = 'COLLABORATOR', CLIENT = 'CLIENT' };

export interface DraftPortfolioOut {
  draftPortfolioId: number,
  name: string,
  portfolioType: 'PORTFOLIO' | 'STRATEGY',
  currency: string,
  riskScore: number,
  strategyRebalance: boolean,
  initialDeposit: number,
  regularInvestment: number,
  weeksBetweenInvestments: 1 | 4 | 12,
  totalYearsToInvest: number,
  messageForClient: string,
  zoojMessage: string,
  autoExpiryTime: string,
  collaborator: ClientCollaboratorOut,
  modelPortfolioInstruments: ModelStrategyOrPortfolioInstrumentOut,
}

export interface InstrumentDetailFullOut {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [index: string]: any,
  instrumentId: number,
  name: string,
  humanName: string,
  isin: string,
  currencyCode: string,
  cash?: boolean,
  instrumentCategory: keyof typeof InstrumentCategories,
  description: string,
  ter: number,
  web: string,
  riskScore: number,
  yield1Y: number,
  yield3Y: number,
  yield5Y: number,
  instrumentTarget: InstrumentTargets,
  instrumentType: InstrumentTypes,
  instrumentIncomeType: IncomeTypes,
  esg: EsgTypes,
  regionalOrientations: string[]
  isInEnglish: boolean,
  rebalancedMonthly: boolean,
  prices1: (number | null)[],
  prices3: (number | null)[],
  prices5: (number | null)[],
  visibleToCollaborator: boolean,
  willBeRemoved: boolean,
  okSmartBond: boolean,
}

export enum EfektikaDepositType {
    REGULAR = 'REGULAR',
    ONE_TIME = 'ONE_TIME',
}

export interface CreateDraftStrategyBase {
  name: string,
  currencyCode: string,
  weeksBetweenInvestments: number,
  totalYearsToInvest: number,
  rebalance: boolean,
  message: string,
  zoojMessage: string,
  investmentGoal: InvestmentGoals,
  draftInitiator: string,
  feeConfigId: number,
}

export interface CreateDraftStrategyIn extends CreateDraftStrategyBase {
  instruments: Record<string, number>,
  initialDeposit: number,
  regularDeposit: number,
}

export interface CreateDraftEfektikaStrategyIn extends CreateDraftStrategyBase {
  deposit: number,
  type: EfektikaDepositType,
}

export type FeeTypes = 'BASIC' | 'EXPRESS' | 'PROPORTIONAL' | 'INITIAL_EXPRESS';

export const TABLE_FEE_TYPES: Record<string, string> = {
  ENTRY: 'entry',
  EXIT: 'exit',
  PERFORMANCE: 'performance',
  MANAGEMENT: 'management',
  VARIABLE_SERVICE: 'variableService',
}

export enum DraftPortfolioStatus {
  SENT = 'SENT',
  EXPIRED = 'EXPIRED',
  ACCEPTED = 'ACCEPTED',
  REFUSED = 'REFUSED'
}

export interface DraftStatusResponse {
  draftPortfolioId: number,
  draftPortfolioStatus: DraftPortfolioStatus,
  portfolioId: number | null
}

export enum ClientAccountAdditionNewIn {
  PM_TO_EO_CONVERSION = 'PM_TO_EO_CONVERSION',
  INVEST_QUEST_INSTANT_CATEGORY = 'INVEST_QUEST_INSTANT_CATEGORY',
  OPF_TRANSFER = 'OPF_TRANSFER',
}

export enum DisabledAdditionReasons {
  WAITING_FOR_SIGNATURE = 'WAITING_FOR_SIGNATURE',
  WAITING_FOR_PROCESSING = 'WAITING_FOR_PROCESSING',
  NOT_EXECUTION_ONLY_CATEGORIES = 'NOT_EXECUTION_ONLY_CATEGORIES',
  NO_PORTFOLIO_TO_TRANSFER = 'NO_PORTFOLIO_TO_TRANSFER',
  EXECUTION_ONLY_NO_ADDED_VALUE = 'EXECUTION_ONLY_NO_ADDED_VALUE',
  NOT_ENGLISH_SPEAKER = 'NOT_ENGLISH_SPEAKER',
  NOT_ALLOWED_BY_COLLABORATOR  = 'NOT_ALLOWED_BY_COLLABORATOR',
  NO_FRAMEWORK_CONTRACT_TO_TRANSFER = 'NO_FRAMEWORK_CONTRACT_TO_TRANSFER',
}

export interface ClientAccountAdditionOut {
  type: ClientAccountAdditionNewIn,
  reasonWhyNotList: DisabledAdditionReasons[],
}

export interface ConsultantState {
  value: ConsultantModalInput | null,
  status: ConsultantStatus;
}

export interface ConsultantImageState {
  value: SafeUrl | null,
  status: ConsultantStatus;
}

export enum ConsultantStatus {
  BLANK = 'BLANK',
  NOT_FOUND = 404,
  LOADED = 'LOADED',
}

export interface ColClientAccountOverviewOut {
  firstName: string,
  lastName: string,
  birthDate: string,
  totalAmount: number,
  lastDepositDate: string,
  clientAccountId: number,
  currencyCode: string,
  validContracts: FrameworkContractValidOut[],
}

export interface FrameworkContractValidOut {
  frameworkContractId: number,
  dip: boolean,
}

export interface ExpectedApiWorkplaceMissingRegular {
  firstName: string,
  lastName: string,
  birthDate: string,
  dip: boolean,
  totalAmount: number,
  clientAccountId: number,
  currencyCode: string,
  portfolios: MissingRegularPortfolio[],
}

export interface MissingRegularPortfolio {
  portfolioId: number,
  name: number,
  frameworkContractNumber: string,
  expectedTargetAmount: number,
  lastRegularDepositDate: string,
  missingRegularDepositAmount: number,
  currencyCode: string,
  managementType: ManagementTypesEnum,
}

export interface ExpectedApiWorkplaceExpiring {
  clientAccountId: number,
  firstName: string,
  lastName: string,
  hasDip?: boolean,
  totalAmount?: number,
  birthDate?: string,
  lastDeposit?: string,
  currencyCode?: string,
  portfolios: ExpiringInvestment[],
}

export interface ExpiringInvestment {
  portfolioId: number,
  name: string,
  targetAmount: number,
  totalAmount: number,
  amountToReachTarget: number,
  investedRatio: number,
  paymentFrequency?: string,
  currencyCode?: string,
  managementType: ManagementTypesEnum,
}

export interface PushNotificationsRequestInput {
  deviceToken: string,
  platform: ApiDevicePlatforms,
}

export enum CapacitorPlatforms {
  ANDROID = 'android',
  IOS = 'ios',
}

export enum ApiDevicePlatforms {
  ANDROID = 'ANDROID',
  IOS = 'IOS',
}

export interface ApiPushNotification {
  notificationWebId: number,
  portfolioId?: number,
  blogId?: number,
  from: number, // Firebase project ID
  type: PushNotificationEventTypes,
  currency?: string,
  portfolioName?: string,
  portfolioTransactionId?: number,
  amount?: number,
  portfolioManagementType?: ManagementTypesEnum,
  collapse_key?: string // If multiple notifications with the same collapse_key are received, only the most recent one will be shown to the user
  google?: { // todo - probably Android object only?
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // undocumented object wildcard
    original_priority: 'normal' | 'high',
    delivered_priority: 'normal' | 'high',
  }
}

export enum PushNotificationEventTypes {
  ADDED_DEPOSIT = 'ADDED_DEPOSIT',
  ADDED_DEPOSIT_LESS_THEN_CASH_THRESHOLD = 'ADDED_DEPOSIT_LESS_THEN_CASH_THRESHOLD',
  PORTFOLIO_DECLINE = 'PORTFOLIO_DECLINE',
  PORTFOLIO_LIQUIDATED = 'PORTFOLIO_LIQUIDATED',
  TRADING_COMPLETED = 'TRADING_COMPLETED',
  WITHDRAWAL_EXECUTION = 'WITHDRAWAL_EXECUTION',
  WITHDRAWAL_REQUEST_ALL = 'WITHDRAWAL_REQUEST_ALL',
  WITHDRAWAL_REQUEST_PART = 'WITHDRAWAL_REQUEST_PART',
  BLOG_CONFIRMED = 'BLOG_CONFIRMED',
}

export interface PortfolioInstrumentSummaryOut {
  portfolioId: number,
  totalAmount: number,
  currencyCode: string,
  instruments: PortfolioInstrumentOut[],
}

export interface PortfolioInstrumentOut {
  instrument: InstrumentDetailFullOut,
  amount: number,
  amountRatio: number,
}

export interface DropdownPortfolioRatios {
  amount: number,
  amountRatio: number,
  weight: number,
}

export enum SlideDirections {
  PREV = 'PREV',
  NEXT = 'NEXT',
}

export enum PriceMeasurements {
  TWR = 'TWR',
  VALUE = 'VALUE',
}

export interface EntryFeeCalculationIn {
  frameworkContractId?: number;
  feeConfigId?: number;
  riskScore: number;
  instruments: Record<string, number>;
  initialDeposit: number;
  regularDeposit: number;
  totalYearsToInvest: number;
  weeksBetweenInvestments: number;
}

export interface EntryFeeCalculationWithAllRatiosOut {
  feeRatio: number;
  entryRegularFeeIncludingVat: number;
  entryRegularFeeVat: number;
  entryRegularRatio: number;
  discountRegularDeposit: number;
  entryOneTimeRatio: number;
  entryOneTimeFeeIncludingVat: number;
  entryOneTimeFeeVat: number;
  mngFeeRatio: number;
  exitFeeRatio: number;
  performanceFeeRatio: number;
  serviceFeeRatio: number;
  entryFeeType: FeeTypes,
  exitFeeRatioYear1: number,
  exitFeeRatioYear2: number,
  exitFeeRatioYear3: number,
  exitFeeRatioYear4: number,
  exitFeeRatioYear5: number,
}

export interface PortfolioFeeConfigsOut {
  entryFeeInfo?: EntryFeeInfo,
  regularEntryFeeConfig: RegularEntryFeeConfig,
  oneTimeEntryFeeConfig: {
    entryFee: number,
  }
  entryFeeConfig: {
    entryFeeType: FeeTypes,
    entryDiscountRegular: number,
    entryFee: number,
  },
  exitFeeConfig: {
    exitFee: number,
  },
  performanceFeeConfig: {
    performanceFee: number,
  },
  mngFeeConfig: {
    mngFee: number,
  },
  variableServiceFeeConfig: {
    variableServiceFee?: number,
  },
  opfFeeImplementation: boolean,
  hasVat: boolean,
}

export interface RegularEntryFeeConfig {
  entryFeeType: FeeTypes,
  entryDiscountRegular: number,
  entryFee: number,
};

export interface EntryFeeInfo {
  totalEntryFee: number,
  entryFeeAlreadyPaid: number,
  vatAlreadyPaid: number,
  entryFeeAlreadyPaidWithVat: number,
  unpaidAmountWithVat: number,
  withVat: boolean,
  totalTargetRegularAmount: number,
  totalRegularAmount: number,
  remainingAmountToReachTargetActual: number,
}

export interface ClientRequestInput {
  portfolioId: number,
  clientId?: number,
}

export interface FeeConfigItemOut {
  [index: string]: number | FeeTypes,
  management: number,
  entry: number,
  exit: number,
  performance: number,
  entryDiscountRegular: number,
  entryFeeType: FeeTypes,
  riskScore: number,
  oneTimeEntry: number,
  variableServiceFee: number,
}

export interface AccessToken {
  access_token: string,
  token_type: string,
  expires_in: number,
  refresh_token: string,
}

export type RiskScoreValue = null | 1 | 2 | 3 | 4 | 5 | 6;
export type LastYearPeriodValue = null | 1 | 3 | 5;
export type InvestmentFrequencyType = typeof InvestmentFrequency[keyof typeof InvestmentFrequency];
export interface WBIMap {
  value: InvestmentFrequency,
  numeric: number,
}

export interface GenerateDocumentLinkOut {
  filename: string,
  link: string,
}

export interface InstrumentGroup {
  groupName: string,
  groupPercentage: number,
  instruments: {
    instrumentName: string
    instrumentPercentage: number
  }[],
}

export interface UpdatePortfolioV2Input {
  newPortfolioName: string,
  initialDeposit: number,
  regularDeposit: number,
  weeksBetweenInvestments: InvestmentFrequency,
  totalYearsToInvest: number,
}

export interface ValidVersionOut {
  valid: boolean;
}

export interface EfektikaActiveOut {
  activeSince?: string | null; // YYYY-MM-DD
}

export interface EmailVerificationOut {
  reasonWhyNotList: EmailConfirmErrors,
  type: EmailConfirmTypes,
}

export enum EmailConfirmTypes {
  DIP_REGULAR_WITHDRAWAL = 'DIP_REGULAR_WITHDRAWAL',
  REGULAR_WITHDRAWAL_DRAFT_TO_DIP = 'REGULAR_WITHDRAWAL_DRAFT_TO_DIP',
}

export enum EmailConfirmErrors {
  TOKEN_EXPIRED = 'TOKEN_EXPIRED',
  TOKEN_INVALIDATED = 'TOKEN_INVALIDATED',
  TOKEN_ALREADY_CONFIRMED = 'TOKEN_ALREADY_CONFIRMED',
}

export interface DraftWithdrawalAcceptIn {
  regularWithdrawalDraftId: number | string,
  portfolioId: number | string,
}

export interface RegularWithdrawalDraftsOut {
  aml: boolean,
  regularWithdrawalDrafts: RegularWithdrawalDraft[],
}

export interface RegularWithdrawalDraft {
  regularWithdrawalDraftId: number,
  portfolioId: number,
  dip: boolean,
  portfolioName: string,
  bankAccountId: number,
  accountNumber: string,
  bankCode: string,
  mode: RegularWithdrawalModes,
  value: number,
  frequency: RegularWithdrawalFrequencies,
  status: RegularWithdrawalDraftStatus,
  collaborator: ClientCollaboratorOut,
  message: string,
  expiredAt: string,
  validTo: string,
}

export interface ExportTransactionsIn {
  clientId : string | number,
  optDateFrom: string,
  optDateTo: string,
  transactionTypes: ApiUiTransactionTypes[],
}

export interface SelectItemCheckbox {
  label: string,
  value: boolean,
  apiKey: string,
}
